import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";

import MaterialTable from "@material-table/core";
import MaterialTableBody from "../../../controls/Material-Table-Components/MaterialTableBody";
import MaterialTableBodyRow from "../../../controls/Material-Table-Components/MaterialTableBodyRow";

import SystemContext from "../../../SystemContext";
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import AirportFuelRestrictionValidator from "../validators/AirportFuelRestrictionValidator";
import {ComboSelect, DatePicker, MaskedText, MaskedNumber} from '../../../controls';
import { useAirportFuelRestrictions, useAddAirportFuelRestriction, useDeleteAirportFuelRestriction, useUpdateAirportFuelRestriction } from "../../../services/airports";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import AirportsLookup from './AirportsLookup';
import useDimensions from '../../../blocks/useDimensions';
import { useTheme } from '@material-ui/core';

const dateFormat = "DD/MM/YYYY";


const AirportFuelRestrictionEditor = (_props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();


  const props = {
    ..._props
  }
  const {classes} = props;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const mode = props.mode || 'FULL';
  const {month, year} = props;
  const airportCode = props.airportCode;

  const handleAddFuelRestriction = useAddAirportFuelRestriction();
  const handleUpdateFuelRestriction = useUpdateAirportFuelRestriction();
  const handleDeleteFuelRestriction = useDeleteAirportFuelRestriction();

  const queryClient = useQueryClient();
  const extraKeys = mode==='FULL'?[month, year]:[airportCode];
  const airportFuelRestrictions = useAirportFuelRestrictions({tableRef, queryClient, extraKeys});

  const minDate = moment.utc();
  const initialDate = minDate.toISOString();

  const eventBus = SystemData.eventBus;
  if (eventBus) {
    eventBus.bind('AirportFuelRestriction.AddLine', (action)=> {
      if (tableRef && tableRef.current) {
        if (action && action === 'edit') {
          setState({...state, canEdit: true});
        } else if (action && action === 'cancel') {
          if (tableRef.current.state.showAddRow) {
            tableRef.current.dataManager.changeRowEditing();
            tableRef.current.setState({
              ...tableRef.current.dataManager.getRenderState(),
              showAddRow: !tableRef.current.state.showAddRow,
            });
          }
          setState({...state, canEdit: false});
        } else {
          tableRef.current.dataManager.changeRowEditing();
          tableRef.current.setState({
            ...tableRef.current.dataManager.getRenderState(),
            showAddRow: !tableRef.current.state.showAddRow,
          });
          setState({...state, canEdit: true});
        }
      }
    });
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const formatChars={
      "U": "[A-Z]",
      "X": "[A-Za-z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9]-",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const validatorColumn = ValidatorColumn(AirportFuelRestrictionValidator, false, {mode});

    const columns = [{...validatorColumn}];
    if (mode === 'FULL') {
      columns.push({
        ...baseColumn,
        ...(ColumnWidth(120, undefined, undefined, {paddingLeft: 2, paddingTop: 8}, {paddingLeft: 2})),
        title: "Airport",
        field: "airportCode",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportsLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      })
    };

    return [...columns, 
      {
        ...baseColumn,
        ...ColumnWidth(160,[5,2],undefined,undefined,undefined,undefined,false),
        title: "Type",
        field: "restrictionType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "FULL",
        initialEditValue: "FULL",
        fullWidth: true,
        sorting: false,
        render: rowData => (
          <ComboSelect
            group="FUEL RESTRICTION TYPES"
            subGroup=""
            value={rowData.restrictionType}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
            <ComboSelect
              group="FUEL RESTRICTION TYPES"
              subGroup=""
              value={props.value}
              onChange={props.onChange}
            />
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(140,[5,2],undefined,undefined,undefined,undefined,false),
        title: "Max Fuel (USG)",
        field: "restrictedQuantityUsg",
        type: "numeric",
        emptyValue: 0,
        initialEditValue: 0,  
        editable: (cell,rowData,updatedData) => (state.canEdit && updatedData.restrictionType !== 'FULL'),
        editComponent: (props) => (
          <MaskedNumber
            displayType="input"
            value={props.value}
            decimalScale={0}
            fixedDecimalScale={true}
            defaultValue={props.value||0}
            valueCap={100000}
            suffix=" usg"
            style={{ fontSize: "0.875rem", textAlign: "right" }}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
            onChange={props.onChange}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.restrictedQuantityUsg}
            decimalScale={0}
            fixedDecimalScale={true}
            suffix=" usg"
            style={{ fontSize: "0.875rem", textAlign: "right" }}
            inputProps={{ style:{ fontSize: "0.875rem", textAlign: "right" }}}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(130,2,undefined, {paddingTop: 10},undefined,undefined,false),
        title: "Effective From",
        field: "fromDate",
        sorting: false,
        emptyValue: initialDate || minDate,
        initialEditValue: initialDate || minDate,  
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            initialValue={minDate}
            minDate={minDate}
            defaultValue={initialDate}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={(fromDate)=> {
              const record = props.rowData;
              props.onRowDataChange({...record, fromDate , toDate: moment.utc(fromDate).isAfter(moment.utc(record.toDate)) ? fromDate : record.toDate});
            }}
          />
        ),
        render: (rowData) => (
          <div>{moment(rowData.fromDate).format(dateFormat)}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(130,2,undefined,{paddingTop: 10},undefined,undefined,false),
        title: "Effective To",
        field: "toDate",
        sorting: false,
        emptyValue: initialDate,
        initialEditValue: initialDate,  
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            initialValue={props.rowData.fromDate || initialDate}
            minDate={props.rowData.fromDate || minDate}
            defaultValue={props.rowData.fromDate || initialDate}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <div>{moment(rowData.toDate).format(dateFormat)}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(undefined,2,undefined,{width: "100%"},undefined,undefined,false),
        title: "Details",
        field: "restrictionDetails",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(250)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
    ];
  };

  const bodyRef = createRef();
  const [tableContainerRef, {height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:36;
  const bodyHeight = tableHeight - thead;

  console.log(tableHeight, thead, bodyHeight);

  return (isAuthenticated &&
      <div ref={tableContainerRef} className={state.canEdit?classes.fuelRestrictionEditor:classes.fuelRestrictions} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 60}px)`}}>
          <MaterialTable
            tableRef={tableRef}
            data={airportFuelRestrictions.data}
            style={{
              backgroundColor: "#fdfdfd",
              boxShadow: "none",
              border: "0px #aaaaaa",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: mode === "FULL",
              cellStyle: {
                padding: "8px 2px",
              },
              headerRowStyle: {
                display: "table",
                width: "100%",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              showTitle: mode === "FULL",
              toolbar: mode === "FULL",
              padding: 'dense',
              paging: false,
              filtering: false,
              draggable: false,
              addRowPosition: 'first',
              onEditModeChanged: (editMode)=> {
                if (props.changeButtonState) {
                  const changes = {
                    save: {disabled: false, hidden: false},
                    send: {disabled: false, hidden: false},
                  }
                  if (editMode) {
                    changes['save'] = {disabled: true, hidden: false}
                    changes['send'] = {disabled: true, hidden: false}
                  }
                  props.changeButtonState(changes);
                }
              },
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  ref={bodyRef}
                  rowHeight={46}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  bodyHeight={mode==='FULL'?bodyHeight:200}
//                  bodyHeight={bodyHeight}
                  headerHeight={46}
                />
              ),
              Row: (props) => (
                <MaterialTableBodyRow
                  {...props}
                />
              ),
            }}
            title="Airport Fuel Restrictions"
            columns={getColumns({...props, canEdit: state.canEdit})}
            actions={[
            ]}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  newData["airportCode"] = airportCode;
                  handleAddFuelRestriction.mutate({record: newData, resolve, reject, extraKeys});
                })
                : undefined,
              onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  handleUpdateFuelRestriction.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                }),
              onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  handleDeleteFuelRestriction.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                }),
            }}
        />
    </div>
  );
}

export default AirportFuelRestrictionEditor;
